import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client'
import {
    ShowGlobalSnackbarMutation,
    ShowGlobalSnackbarMutationProps,
    ShowGlobalSnackbarRequest,
} from '@deltek/specification-client/core/Configurations/GlobalSnackbar'
import { ProductListingDetailsAutoSuggestQuery } from 'api/graphQL/queries/productSearch.queries'
import { ProductListingDetailsAutoSuggestResult } from 'api/graphQL/types/product.types'
import React, { FC, useMemo } from 'react'
import { RadioType } from './AddProductTypes'
import ExistingProductTypeAutoCompleteInput from './ExistingProductTypeAutoCompleteInput'
import NewProductTypeSection from './NewProductTypeSection'
import ProductTypeRadioSection from './ProductTypeRadioSection'
import { addProductDialogState } from './addProductFormHandler'

const ProductTypeSection: FC = () => {
    const {
        sectionNames,
        sectionNumber,
        sectionTitle,
        categories,
        firmName,
        familyRadio,
        productTypeRadio,
    } = useReactiveVar(addProductDialogState)

    const [showGlobalSnackbar] = useMutation<
        ShowGlobalSnackbarRequest,
        ShowGlobalSnackbarMutationProps
    >(ShowGlobalSnackbarMutation)

    const [
        callAutoSuggest,
        { loading: autoSuggestLoading, data: autoSuggestData },
    ] = useLazyQuery<ProductListingDetailsAutoSuggestResult>(
        ProductListingDetailsAutoSuggestQuery,
        {
            fetchPolicy: 'network-only',
            onError: () => {
                showGlobalSnackbar({
                    variables: {
                        message: 'Something went wrong with autosuggest',
                        variant: 'error',
                        id: 'autosuggest_Error',
                    },
                })
            },
        }
    )

    const isProductTypeDisabled = useMemo(
        () =>
            !firmName ||
            (familyRadio === RadioType.Existing
                ? !sectionNames
                : !sectionNumber || !sectionTitle || !categories.length),
        [
            firmName,
            sectionNames,
            sectionNumber,
            sectionTitle,
            familyRadio,
            categories,
        ]
    )

    return (
        <>
            <ProductTypeRadioSection />
            {productTypeRadio === RadioType.Existing ? (
                <ExistingProductTypeAutoCompleteInput
                    autoSuggestData={autoSuggestData}
                    callAutoSuggest={callAutoSuggest}
                    autoSuggestLoading={autoSuggestLoading}
                    disabled={isProductTypeDisabled}
                />
            ) : (
                <NewProductTypeSection
                    autoSuggestData={autoSuggestData}
                    callAutoSuggest={callAutoSuggest}
                    disabled={isProductTypeDisabled}
                />
            )}
        </>
    )
}

export default ProductTypeSection
